import { Timestamp } from '@lemon/core/model/common'

/** Lähdejärjestelmä, josta tiedosto on peräisin. Tiedoston muoto (XLSX/CSV/jne.) on lähteen sisäinen yksityiskohta. */
export type TuoteImportLahdejarjestelma = 'netvisor' | 'procountor'

/** Virhekoodit tuote-import-operaatioista. Frontend tulkkaa nämä käyttäjälle näytettäviksi teksteiksi. */
export enum TuoteImportVirhekoodi {
  METADATA_EI_LOYDY = 'metadata-ei-loydy',
  TIEDOSTOA_EI_LOYDY = 'tiedostoa-ei-loydy',
  OTSIKKORIVI_PUUTTUU = 'otsikkorivi-puuttuu',
  SARAKKEET_PUUTTUVAT = 'sarakkeet-puuttuvat',
  EI_TUOTTEITA = 'ei-tuotteita',
  PARSINTAVIRHE = 'parsintavirhe',
  LAHDEJARJESTELMA_EPAVARMA = 'lahdejarjestelma-epavarma',
  TUNTEMATON_VIRHE = 'tuntematon-virhe',
}

/** Sisäinen tietomalli: yksi lähdetiedostosta parsittu tuoterivi (ennen tallennusta). */
export interface TuoteImportRivi {
  /** Alkuperäinen rivinumero lähdetiedostossa (1-pohjainen, otsikkorivi ei lasketa) */
  rivinumero: number
  /** Ulkoinen tunniste lähdejärjestelmässä (esim. Netvisorin Tuotekoodi). Tyhjä jos lähde ei tarjoa tunnistetta. */
  ulkoinenTunniste: string
  /** Tuotteen nimi */
  nimi: string
  /** Yksikköhinta (Netvisorilla veroton nettohinta) */
  hinta: number
  /** Yksikkö esim. 'kpl', 'h', 'kk'. Tyhjä jos ei tarjolla. */
  yksikko: string
  /** ALV-prosentti (esim. 0, 10, 14, 24, 25.5) — null jos puuttuu. */
  alvProsentti: number | null
  /** Vapaa kuvausteksti */
  kuvaus: string
}

/** Pyyntö tiedoston parsimiseksi. Tiedosto on jo arkistossa (uploadattu hallintokansiossa). */
export interface TuoteImportParsePyynto {
  asiakasAvain: string
  /** Arkistotiedoston metadata-avain (ArkistoFile.avain) */
  arkistoTiedostonAvain: string
  /** Jos puuttuu, backend tunnistaa lähdejärjestelmän tiedoston sisällöstä. */
  lahdejarjestelma?: TuoteImportLahdejarjestelma
}

/** Vastaus parsinnasta. Sisältää esikatselurivit, ei tallenna mitään. */
export interface TuoteImportParseVastaus {
  onnistui: boolean
  virhekoodi?: TuoteImportVirhekoodi
  /** Virheviesti parsinnasta käyttäjälle näytettäväksi (käännetty Suomeksi). Backend täyttää PARSINTAVIRHE-tapauksissa. */
  virheviesti?: string
  /** Parsitut tuoterivit esikatselua varten */
  rivit?: TuoteImportRivi[]
  /** Parsinnassa käytetty lähdejärjestelmä (pyynnössä annettu tai tiedostosta tunnistettu) */
  lahdejarjestelma?: TuoteImportLahdejarjestelma
}

/** Pyyntö esikatseltujen tuotteiden tallentamiseksi asiakkaan tuoterekisteriin. */
export interface TuoteImportTallennaPyynto {
  asiakasAvain: string
  /** Tallennettavat tuoterivit (frontend voi suodattaa esikatselun riveistä) */
  rivit: TuoteImportRivi[]
  /** Sama arkistotiedoston avain kuin parse-vaiheessa — kirjataan TuoteImportDocumenttiin viimeisimmän tuonnin lähteeksi. */
  arkistoTiedostonAvain: string
  lahdejarjestelma: TuoteImportLahdejarjestelma
}

/** Vastaus tuotteiden tallennuksesta. */
export interface TuoteImportTallennaVastaus {
  onnistui: boolean
  virhekoodi?: TuoteImportVirhekoodi
  /** Montako uutta tuotetta kirjoitettiin Firestoreen */
  tallennetut?: number
  /** Montako riviä ohitettiin duplikaattina (sama ulkoinenTunniste löytyi jo asiakkaan tuoterekisteristä tai tuontitiedostosta) */
  ohitetutDuplikaatit?: number
}

/**
 * Asiakkaan tallennettu tuote-import-dokumentti — viimeisimmän tuonnin metadata.
 * Firestore-polku: /asiakkaat/{asiakasAvain}/muut/tuote-import
 */
export interface TuoteImportDocument {
  tuotu: Timestamp
  lahdejarjestelma: TuoteImportLahdejarjestelma
  tuoja: string
  arkistoTiedostonAvain: string
  tiedostonNimi: string
  tallennetutTuotteet: number
  /** Montako duplikaattia ohitettiin tuonnissa. Tallennetaan myös TuoteImportDocumenttiin jotta yhteenveto näkyy hallintatyökaluissa. */
  ohitetutDuplikaatit?: number
}

/**
 * Yksittäisen tuonnin historiamerkintä — yksi rivi per käyttäjän käynnistämä tallennus.
 * Firestore-polku: /asiakkaat/{asiakasAvain}/muut/tuote-import/tuote-import-historia/{historiaAvain}
 */
export interface TuoteImportHistoriaDocument extends TuoteImportDocument {
  /** Historiamerkinnän avain (kollektion docId). Sama kuin Firestore-polun viimeinen segmentti. */
  avain: string
}
